import { inject, Injectable, NgZone, OnDestroy, signal } from '@angular/core';
import {
  PtsPayterTerminal,
  TerminalSessionResponse,
  TerminalSessionSearchEvent,
  TerminalSessionStatusEnum,
} from '../pts-payment-terminal-model';
import { PtsPaymentTerminalService } from 'src/app/shared/services/payment-terminal.service';
import { BaseListStateService, IFilterControls } from 'src/app/shared/models/base.state.service';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { PagedResult } from 'src/app/shared/models/paging';
import { SnackbarService } from 'ok-angular-shared/components';
import { Firestore, doc, DocumentReference, DocumentData } from '@firebase/firestore';
import moment from 'moment';
import { FIRESTORE_INSTANCE } from 'src/app/shared/services/firestore-instance.token';
import { observeDocData } from 'src/app/shared/services/firestore-observables';

interface IPtsSessionOverviewFilterModel {
  terminalSerialNumber: string | null;
  query: string | null;
  startDate: string | null;
  endDate: string | null;
}
export enum PtsSessionListQueryParams {
  query = 'query',
  terminalSerialNumber = 'terminalSerialNumber',
  startDate = 'startDate',
  endDate = 'endDate',
}
@Injectable()
export class PtsSessionOverviewStateService
  extends BaseListStateService<PtsPayterTerminal, PtsSessionListQueryParams, IPtsSessionOverviewFilterModel>
  implements OnDestroy
{
  private service = inject(PtsPaymentTerminalService);
  private snackbarService = inject(SnackbarService);
  private firestore = inject<Firestore>(FIRESTORE_INSTANCE);
  private ngZone = inject(NgZone);
  minutesThreshold = 5;
  minutesLargeThreshold = 1440;

  ngOnDestroy(): void {
    this.sessionsSubscription?.unsubscribe();
    this.firestoreSubscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
  private moment = moment;
  public firestoreSubscriptions: Subscription[] = [];
  private sessionsSubscription?: Subscription;
  public resetLocationsTrigger = signal<{ reset: boolean }>({ reset: false });
  public resetTerminalsTrigger = signal<{ reset: boolean }>({ reset: false });
  public sessions = signal<PagedResult<TerminalSessionResponse>>({ items: [], totalCount: 0, pageIndex: 0, pageSize: 0 });

  private queryParamValue(key: PtsSessionListQueryParams): string | null {
    return this.queryParamMap$()?.get(key) ?? null;
  }

  protected createQueryParams(filters: Partial<IPtsSessionOverviewFilterModel>): Record<PtsSessionListQueryParams, string | null> {
    const queryParams: Record<PtsSessionListQueryParams, string | null> = {
      [PtsSessionListQueryParams.terminalSerialNumber]: filters.terminalSerialNumber
        ? encodeURIComponent(filters.terminalSerialNumber)
        : null,
      [PtsSessionListQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
      [PtsSessionListQueryParams.startDate]: filters.startDate ? encodeURIComponent(filters.startDate) : null,
      [PtsSessionListQueryParams.endDate]: filters.endDate ? encodeURIComponent(filters.endDate) : null,
    };
    return queryParams;
  }
  form: FormGroup<IFilterControls<IPtsSessionOverviewFilterModel>> = new FormGroup<IFilterControls<IPtsSessionOverviewFilterModel>>({
    query: new FormControl<string | null>(
      this.queryParamValue(PtsSessionListQueryParams.query)
        ? decodeURIComponent(this.queryParamValue(PtsSessionListQueryParams.query) as string)
        : null
    ),
    terminalSerialNumber: new FormControl<string | null>(
      this.queryParamValue(PtsSessionListQueryParams.terminalSerialNumber)
        ? this.queryParamValue(PtsSessionListQueryParams.terminalSerialNumber)
        : null
    ),
    startDate: new FormControl<string | null>(this.queryParamValue(PtsSessionListQueryParams.startDate)),
    endDate: new FormControl<string | null>(this.queryParamValue(PtsSessionListQueryParams.endDate)),
  });

  $sessions: Observable<PagedResult<TerminalSessionResponse>> = combineLatest([
    toObservable(this.refreshTrigger),
    this.form.valueChanges.pipe(startWith(this.form.value)),
    toObservable(this.viewState),
  ]).pipe(
    takeUntilDestroyed(),
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this.loading.set(true)),
    switchMap(([refresh, filters, viewState]) => {
      const filter = <TerminalSessionSearchEvent>{
        paging: viewState.page,
        sorting: viewState.sort,
        query: filters.query,
        serialNumber: filters.terminalSerialNumber,
        start: filters.startDate,
        end: filters.endDate,
      };
      if (filters.startDate && filters.endDate) {
        filter.start = moment(filters.startDate).format('YYYY-MM-DD HH:mm:ss');
        filter.end = moment(filters.endDate).add(1, 'days').format('YYYY-MM-DD HH:mm:ss');
      }
      return this.service.getTerminalSessions(filter).pipe(
        catchError(err => {
          this.snackbarService.error(err?.error.message);
          return of(<PagedResult<TerminalSessionResponse>>{ items: [], totalCount: 0, pageIndex: 0, pageSize: 0 });
        })
      );
    }),
    map(sessions => {
      this.firestoreSubscriptions.forEach(subscription => subscription.unsubscribe());
      this.firestoreSubscriptions = [];

      sessions.items.forEach(session => {
        session.lastActivity = session.updated;
        const minutesSinceActivity = moment().diff(moment(session.lastActivity), 'minutes');

        session.enabledCancelReservationAmount =
          !!session.payterSessionId &&
          ((session.status === TerminalSessionStatusEnum.Initial && minutesSinceActivity > this.minutesThreshold) ||
            (session.status === TerminalSessionStatusEnum.PaymentReserved && minutesSinceActivity > this.minutesLargeThreshold));

        if (session.chargingToken) {
          const docRef = doc(this.firestore, `OK/Emsp/RemoteTransactions/${session.chargingToken}`) as DocumentReference<DocumentData>;

          const subscription = observeDocData(docRef, this.ngZone).subscribe(transaction => {
            const firestoreRemoteTransaction = {
              chargeInWh: transaction?.chargeInWh,
              powerInW: transaction?.powerInW,
              powerUpdated: transaction?.powerUpdated?.toDate(),
              status: transaction?.status,
              statusUpdated: transaction?.statusUpdated?.toDate(),
              totalConsumptionInWh: transaction?.totalConsumptionInWh,
            };

            const lastActivity = firestoreRemoteTransaction.powerUpdated ?? session.lastActivity;
            const minutesSinceActivity = moment().diff(moment(lastActivity), 'minutes');
            const enabledCancelReservationAmount =
              !firestoreRemoteTransaction.status ||
              (firestoreRemoteTransaction.status === 'Charging' && minutesSinceActivity > this.minutesThreshold) ||
              (firestoreRemoteTransaction.status === 'Pending' && minutesSinceActivity > this.minutesLargeThreshold);

            this.sessions.update(currentSessions => ({
              ...currentSessions,
              items: currentSessions.items.map(currentSession =>
                currentSession.id === session.id
                  ? { ...currentSession, firestoreRemoteTransaction, lastActivity, enabledCancelReservationAmount }
                  : currentSession
              ),
            }));
          });
          this.firestoreSubscriptions.push(subscription);
        }
      });
      return sessions;
    }),
    tap(() => this.loading.set(false))
  );

  constructor() {
    super();
    this.resetPageOnFilterChange();
    this.sessionsSubscription = this.$sessions.subscribe(sessions => this.sessions.set(sessions));
  }
}
